<script setup>
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { useBranding } from 'shared/composables/useBranding';
import rasAPI from 'dashboard/api/ras';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import BaseSettingsHeader from '../../components/BaseSettingsHeader.vue';
import SettingsLayout from '../../SettingsLayout.vue';
import RasRuleCard from './components/RasRuleCard.vue';
import RasRuleDialog from './components/RasRuleDialog.vue';

const { t } = useI18n();
const store = useStore();
const { replaceInstallationName } = useBranding();

const isLoadingRules = ref(true);
const isSavingSettings = ref(false);
const isSavingRule = ref(false);
const rasIntegrationActive = ref(false);
const clinicName = ref('');
const assistantName = ref('');
const rules = ref([]);

const selectedRule = ref(null);
const showDialog = ref(false);
const dialogKey = ref(0);

const integration = computed(() =>
  store.getters['integrations/getIntegration']('ras')
);

const loadData = async () => {
  try {
    isLoadingRules.value = true;
    const [settingsRes, rulesRes] = await Promise.all([
      rasAPI.get(),
      rasAPI.getRules(),
    ]);

    rasIntegrationActive.value =
      settingsRes.data.ras_integration_active ??
      Boolean(integration.value?.enabled);
    clinicName.value = settingsRes.data.clinic_name || '';
    assistantName.value = settingsRes.data.assistant_name || '';
    rules.value = rulesRes.data || [];
  } catch {
    useAlert(t('INTEGRATION_SETTINGS.RAS.RULES.API.ERROR_MESSAGE'));
  } finally {
    isLoadingRules.value = false;
  }
};

const saveSettings = async () => {
  try {
    isSavingSettings.value = true;
    await rasAPI.update({
      clinic_name: clinicName.value,
      assistant_name: assistantName.value,
    });
    useAlert(t('INTEGRATION_SETTINGS.RAS.SETTINGS.API.SUCCESS'));
  } catch {
    useAlert(t('INTEGRATION_SETTINGS.RAS.SETTINGS.API.ERROR'));
  } finally {
    isSavingSettings.value = false;
  }
};

const openCreateDialog = () => {
  selectedRule.value = null;
  dialogKey.value += 1;
  showDialog.value = true;
};

const openEditDialog = rule => {
  selectedRule.value = rule;
  dialogKey.value += 1;
  showDialog.value = true;
};

const closeDialog = () => {
  showDialog.value = false;
  selectedRule.value = null;
};

const handleRuleSubmit = async payload => {
  try {
    isSavingRule.value = true;
    if (selectedRule.value) {
      const res = await rasAPI.updateRule(selectedRule.value.id, payload);
      const idx = rules.value.findIndex(r => r.id === selectedRule.value.id);
      if (idx !== -1) rules.value[idx] = res.data;
      useAlert(t('INTEGRATION_SETTINGS.RAS.RULE_FORM.API.UPDATE_SUCCESS'));
    } else {
      const res = await rasAPI.createRule(payload);
      rules.value.push(res.data);
      useAlert(t('INTEGRATION_SETTINGS.RAS.RULE_FORM.API.CREATE_SUCCESS'));
    }
    closeDialog();
    await loadData();
  } catch (error) {
    const msg =
      error?.response?.data?.errors?.join(', ') ||
      t(
        selectedRule.value
          ? 'INTEGRATION_SETTINGS.RAS.RULE_FORM.API.UPDATE_ERROR'
          : 'INTEGRATION_SETTINGS.RAS.RULE_FORM.API.CREATE_ERROR'
      );
    useAlert(msg);
  } finally {
    isSavingRule.value = false;
  }
};

const handleDeleteRule = async rule => {
  if (!window.confirm(t('INTEGRATION_SETTINGS.RAS.RULE_CARD.CONFIRM_DELETE')))
    return;
  try {
    await rasAPI.deleteRule(rule.id);
    rules.value = rules.value.filter(r => r.id !== rule.id);
    useAlert(t('INTEGRATION_SETTINGS.RAS.RULE_FORM.API.DELETE_SUCCESS'));
  } catch {
    useAlert(t('INTEGRATION_SETTINGS.RAS.RULE_FORM.API.DELETE_ERROR'));
  }
};

const BASE_RULE_KEYS = new Set(['id', 'type', 'title', 'active']);

const extractRuleData = rule => {
  const data = {};
  Object.keys(rule).forEach(k => {
    if (!BASE_RULE_KEYS.has(k)) data[k] = rule[k];
  });
  return data;
};

const handleToggleActive = async rule => {
  try {
    const res = await rasAPI.updateRule(rule.id, {
      rule_type: rule.type,
      title: rule.title,
      active: !rule.active,
      rule_data: extractRuleData(rule),
    });
    const idx = rules.value.findIndex(r => r.id === rule.id);
    if (idx !== -1) rules.value[idx] = res.data;
  } catch {
    useAlert(t('INTEGRATION_SETTINGS.RAS.RULES.API.ERROR_MESSAGE'));
  }
};

const sortedRules = computed(() =>
  [...rules.value].sort((a, b) => a.title.localeCompare(b.title))
);

onMounted(async () => {
  await store.dispatch('integrations/get');
  store.dispatch('labels/get');
  await loadData();
});
</script>

<template>
  <SettingsLayout
    :is-loading="isLoadingRules"
    :loading-message="$t('INTEGRATION_SETTINGS.RAS.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        v-if="integration?.name"
        :title="integration.name"
        :description="replaceInstallationName(integration.description)"
        :back-button-label="$t('INTEGRATION_SETTINGS.HEADER')"
        feature-name="ras"
      />
    </template>

    <template #body>
      <section class="flex flex-col gap-6 w-full">
        <p
          v-if="!isLoadingRules && !rasIntegrationActive"
          class="text-sm text-n-slate-11"
        >
          {{ $t('INTEGRATION_SETTINGS.RAS.RULES.INACTIVE_NOTE') }}
        </p>

        <!-- Account settings -->
        <div class="flex flex-col gap-4 p-4 rounded-xl border border-n-weak">
          <h3 class="text-sm font-semibold text-n-slate-12">
            {{ $t('INTEGRATION_SETTINGS.RAS.SETTINGS.TITLE') }}
          </h3>

          <div class="grid grid-cols-2 gap-4">
            <Input
              v-model="clinicName"
              name="clinic_name"
              :label="$t('INTEGRATION_SETTINGS.RAS.SETTINGS.CLINIC_NAME.LABEL')"
              :placeholder="
                $t('INTEGRATION_SETTINGS.RAS.SETTINGS.CLINIC_NAME.PLACEHOLDER')
              "
              :disabled="!rasIntegrationActive"
            />
            <Input
              v-model="assistantName"
              name="assistant_name"
              :label="
                $t('INTEGRATION_SETTINGS.RAS.SETTINGS.ASSISTANT_NAME.LABEL')
              "
              :placeholder="
                $t(
                  'INTEGRATION_SETTINGS.RAS.SETTINGS.ASSISTANT_NAME.PLACEHOLDER'
                )
              "
              :disabled="!rasIntegrationActive"
            />
          </div>

          <div class="flex justify-end">
            <NextButton
              blue
              :label="$t('INTEGRATION_SETTINGS.RAS.SETTINGS.SAVE')"
              :is-loading="isSavingSettings"
              :disabled="!rasIntegrationActive || isSavingSettings"
              @click="saveSettings"
            />
          </div>
        </div>

        <!-- Rules list -->
        <div class="flex flex-col gap-3">
          <div class="flex items-center justify-between">
            <h3 class="text-sm font-semibold text-n-slate-12">
              {{ $t('INTEGRATION_SETTINGS.RAS.RULES.TITLE') }}
            </h3>
            <NextButton
              blue
              icon="i-lucide-plus"
              :label="$t('INTEGRATION_SETTINGS.RAS.RULES.ADD_RULE')"
              :disabled="!rasIntegrationActive"
              @click="openCreateDialog"
            />
          </div>

          <div v-if="sortedRules.length" class="flex flex-col gap-2">
            <RasRuleCard
              v-for="rule in sortedRules"
              :key="rule.id"
              :rule="rule"
              @edit="openEditDialog"
              @delete="handleDeleteRule"
              @toggle-active="handleToggleActive"
            />
          </div>

          <div
            v-else-if="!isLoadingRules"
            class="flex flex-col items-center justify-center py-10 text-center border border-dashed border-n-weak rounded-xl"
          >
            <p class="text-sm text-n-slate-10">
              {{ $t('INTEGRATION_SETTINGS.RAS.RULES.EMPTY_STATE') }}
            </p>
          </div>
        </div>
      </section>
    </template>
  </SettingsLayout>

  <RasRuleDialog
    v-if="showDialog"
    :key="dialogKey"
    :rule="selectedRule"
    :is-saving="isSavingRule"
    @submit="handleRuleSubmit"
    @cancel="closeDialog"
  />
</template>
